'use client';

import { useEffect, useState } from 'react';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { logger } from '@/lib/telemetry/logger';

interface OwnRowVisibility {
  observationKey: string | null;
  visible: boolean;
}

const CURRENT_USER_ROW_SELECTOR = '[data-current-user-row="true"]';
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

type RowScrollMode = 'contained' | 'page-below-lg';

export function useOwnRowVisibility(
  rootRef: React.RefObject<HTMLDivElement | null>,
  observationKey: string | null,
  scrollSelector: string,
  observerFailureEvent: string,
  scrollMode: RowScrollMode = 'contained',
): OwnRowVisibility {
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const [visibility, setVisibility] = useState<OwnRowVisibility>({
    observationKey: null,
    visible: false,
  });

  useEffect(() => {
    if (observationKey === null) return;
    let active = true;
    const publishFallback = (visible: boolean): void => {
      queueMicrotask(() => {
        if (active) setVisibility({ observationKey, visible });
      });
    };
    const container = rootRef.current;
    const scrollRoot = container?.querySelector<HTMLElement>(scrollSelector) ?? null;
    const userRows = container?.querySelectorAll<HTMLElement>(CURRENT_USER_ROW_SELECTOR) ?? [];
    if (scrollRoot === null || userRows.length === 0) {
      publishFallback(false);
      return (): void => {
        active = false;
      };
    }

    const visibleRows = new Set<Element>();
    try {
      const observerRoot = scrollMode === 'page-below-lg' && !isDesktop ? null : scrollRoot;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              visibleRows.add(entry.target);
            } else {
              visibleRows.delete(entry.target);
            }
          }
          setVisibility({ observationKey, visible: visibleRows.size > 0 });
        },
        { root: observerRoot },
      );
      for (const row of userRows) observer.observe(row);
      return (): void => {
        active = false;
        observer.disconnect();
      };
    } catch (error) {
      logger.warn(observerFailureEvent, {
        stage: 'observe_current_user_row',
        errorName: error instanceof Error ? error.name : 'Error',
      });
      publishFallback(true);
      return (): void => {
        active = false;
      };
    }
  }, [isDesktop, observationKey, observerFailureEvent, rootRef, scrollMode, scrollSelector]);

  return visibility;
}
